import { Link } from 'react-router-dom';
import logoImg from '../assets/logo.png';

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100">
      <header className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <img src={logoImg} alt="Logo" className="w-8 h-8" />
            <span className="font-semibold text-lg">Unified LLM Platform</span>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-bold mb-2">Terms &amp; Conditions</h1>
        <p className="text-gray-500 dark:text-gray-400 text-sm mb-10">General terms of use for the Unified LLM Platform</p>

        <section className="mb-10">
          <h2 className="text-xl font-semibold mb-3">1. Scope</h2>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
            These Terms &amp; Conditions govern the use of the Unified LLM Platform operated by Imran Hussain,
            IH Software Services Provider (hereinafter "Operator"). By registering or using this platform you
            agree to these terms in full. Deviating terms of use by the user are not accepted unless expressly
            agreed to in writing by the Operator.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-semibold mb-3">2. Platform Description</h2>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
            The Unified LLM Platform provides a single interface to interact with multiple large language models
            (LLMs) from commercial and open-source AI providers, including but not limited to OpenAI (GPT),
            Anthropic (Claude), Google (Gemini), Mistral, DeepSeek, and Groq/Llama.
          </p>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
            The platform acts as an intermediary. AI responses are generated by third-party providers and the
            Operator does not control, review, or guarantee the content, accuracy, or reliability of any
            AI-generated output.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-semibold mb-3">3. Registration and Account</h2>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
            Access to the platform requires registration with a valid email address. You are responsible for
            keeping your login credentials secure and for all activities that occur under your account.
          </p>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
            You must be at least 18 years of age to use this platform. By registering, you confirm that the
            information you provide is accurate and complete. The Operator reserves the right to suspend or
            terminate accounts that violate these terms.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-semibold mb-3">4. Acceptable Use</h2>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">Users agree not to use the platform to:</p>
          <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2 mb-4">
            <li>Generate content that is illegal, harmful, abusive, discriminatory, or violates the rights of others</li>
            <li>Attempt to circumvent safety filters or alignment measures of any AI model</li>
            <li>Generate or distribute misinformation, deepfakes, or deceptive AI-generated content</li>
            <li>Conduct automated scraping, mass querying, or abuse of API rate limits</li>
            <li>Share account credentials or allow unauthorized third parties to use the service</li>
            <li>Violate the terms of service of any underlying AI provider</li>
            <li>Use the platform for any purpose that violates applicable law</li>
          </ul>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
            The Operator reserves the right to monitor usage for compliance and to take action — including
            account suspension — if these terms are violated.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-semibold mb-3">5. AI-Generated Content</h2>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
            AI models can produce inaccurate, incomplete, biased, or otherwise unreliable outputs. You are
            solely responsible for how you interpret and use AI-generated content. The platform is not a
            substitute for professional advice (medical, legal, financial, or otherwise).
          </p>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
            You retain ownership of the prompts you submit. AI-generated outputs may be subject to the
            intellectual property terms of the respective AI provider. Please review each provider's terms
            regarding ownership of generated content.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-semibold mb-3">6. Rate Limits and Fair Use</h2>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
            The platform enforces rate limits per user to ensure fair access for all users and to manage
            API costs. Default limits are defined by the administrator and may be adjusted on a per-user
            basis. Attempts to circumvent rate limits may result in account suspension.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-semibold mb-3">7. Availability and Service Interruptions</h2>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
            The Operator makes reasonable efforts to ensure platform availability but does not guarantee
            uninterrupted access. Service may be interrupted due to maintenance, third-party provider outages,
            or circumstances beyond the Operator's control. The Operator is not liable for any loss arising
            from service unavailability.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-semibold mb-3">8. Intellectual Property</h2>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
            The platform software, design, and documentation are the intellectual property of IH Software
            Services Provider and are protected by copyright. AI model weights, APIs, and brand names belong
            to their respective providers. Users may not reproduce, distribute, or create derivative works
            from platform content without prior written consent.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-semibold mb-3">9. Limitation of Liability</h2>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
            The Operator is liable without limitation for intent and gross negligence, and for damages
            resulting from injury to life, body, or health.
          </p>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
            For slight negligence, the Operator is only liable for the breach of essential contractual
            obligations. In such cases, liability is limited to the foreseeable, typical damage.
          </p>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
            The Operator is not liable for the accuracy, completeness, or appropriateness of AI-generated
            content, or for decisions made by users based on such content.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-semibold mb-3">10. Data Protection</h2>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
            The handling of personal data is governed by our{' '}
            <Link to="/privacy" className="text-blue-600 dark:text-blue-400 hover:underline">Privacy Policy</Link>,
            which forms part of these Terms &amp; Conditions. By using the platform you consent to the
            processing of your data as described therein.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-semibold mb-3">11. Modifications to Terms</h2>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
            The Operator reserves the right to modify these Terms &amp; Conditions at any time. Users will
            be notified of significant changes via email or a platform notification. Continued use of the
            platform after such notification constitutes acceptance of the revised terms.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-semibold mb-3">12. Termination</h2>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
            Users may delete their account at any time via profile settings. The Operator may terminate or
            suspend access with immediate effect in the event of a serious breach of these terms, illegal
            activity, or abuse of the platform. Upon termination, all user data will be deleted in accordance
            with the Privacy Policy.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-semibold mb-3">13. Governing Law and Jurisdiction</h2>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
            These Terms &amp; Conditions are governed by the law of the Federal Republic of Germany.
            The place of jurisdiction for all disputes arising from the contractual relationship is,
            to the extent permitted by law, the registered office of the Operator (Steinenbronn, Germany).
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-semibold mb-3">14. Severability</h2>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
            Should individual provisions of these Terms &amp; Conditions be or become invalid, the validity
            of the remaining provisions shall not be affected. The invalid provision shall be replaced by a
            valid provision that comes as close as possible to the economic purpose of the invalid provision.
          </p>
        </section>

        <p className="text-sm text-gray-400 dark:text-gray-500 mt-12">Last updated: March 2026</p>
      </main>

      <footer className="border-t border-gray-200 dark:border-gray-800 py-6 px-6 mt-12">
        <div className="max-w-4xl mx-auto flex flex-wrap gap-4 text-sm text-gray-500 dark:text-gray-400">
          <Link to="/imprint" className="hover:text-gray-900 dark:hover:text-white transition-colors">Imprint</Link>
          <Link to="/privacy" className="hover:text-gray-900 dark:hover:text-white transition-colors">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-gray-900 dark:hover:text-white transition-colors">Terms &amp; Conditions</Link>
          <Link to="/" className="hover:text-gray-900 dark:hover:text-white transition-colors">Back to Home</Link>
        </div>
      </footer>
    </div>
  );
}
